import { taxRegexp } from '../constants/CountryData';

/**
 * VAT / tax-id validation shared by every form and brand (any active merchant).
 *
 * Two layers:
 *  1. Format — the EU VAT format regex (`taxRegexp`), per country.
 *  2. Checksum — a country-specific control-digit check, when one is registered
 *     in `vatChecksumValidators`. Countries without an entry fall back to
 *     format-only (add them here as they are implemented).
 *
 * The NIF/VAT field is treated as REQUIRED by default; pass `{ required: false }`
 * to allow an empty value.
 */

/**
 * Portuguese NIF (contribuinte) mod-11 control-digit check.
 * Receives the 9-digit national number, WITHOUT the "PT" prefix.
 */
export const isValidPtNif = (nif: string): boolean => {
  if (!/^\d{9}$/.test(nif)) return false;
  const digits = nif.split('').map((c) => parseInt(c, 10));
  let sum = 0;
  for (let i = 0; i < 8; i += 1) {
    sum += digits[i] * (9 - i);
  }
  const mod = sum % 11;
  const control = mod < 2 ? 0 : 11 - mod;
  return control === digits[8];
};

/**
 * Spanish tax-id control-digit check. Covers the three national formats that
 * the ES VAT regex (`[\dA-Z]\d{7}[\dA-Z]`) admits:
 *  - DNI/NIF (persons):   8 digits + control letter
 *  - NIE (foreigners):    [XYZ] + 7 digits + control letter
 *  - CIF (organisations): org letter + 7 digits + control (digit or letter)
 * Receives the 9-char national number WITHOUT the "ES" prefix.
 */
export const isValidEsVat = (value: string): boolean => {
  const v = (value || '').toUpperCase();
  if (!/^[A-Z0-9]\d{7}[A-Z0-9]$/.test(v)) return false;

  // Control letter used by DNI/NIE (indexed by number mod 23).
  const dniLetters = 'TRWAGMYFPDXBNJZSQVHLCKE';

  // DNI/NIF — 8 digits + letter.
  if (/^\d{8}[A-Z]$/.test(v)) {
    return dniLetters[parseInt(v.slice(0, 8), 10) % 23] === v[8];
  }

  // NIE — X/Y/Z (→ 0/1/2) + 7 digits + letter.
  if (/^[XYZ]\d{7}[A-Z]$/.test(v)) {
    const prefixDigit = { X: '0', Y: '1', Z: '2' }[v[0] as 'X' | 'Y' | 'Z'];
    return dniLetters[parseInt(prefixDigit + v.slice(1, 8), 10) % 23] === v[8];
  }

  // CIF — org letter + 7 digits + control (Luhn-like on the 7 central digits).
  if (/^[ABCDEFGHJNPQRSUVW]\d{7}[0-9A-J]$/.test(v)) {
    const digits = v.slice(1, 8).split('').map((c) => parseInt(c, 10));
    let sum = 0;
    for (let i = 0; i < digits.length; i += 1) {
      if (i % 2 === 0) {
        const doubled = digits[i] * 2;
        sum += doubled < 10 ? doubled : doubled - 9;
      } else {
        sum += digits[i];
      }
    }
    const control = (10 - (sum % 10)) % 10;
    const controlChar = v[8];
    const orgLetter = v[0];
    const controlLetter = 'JABCDEFGHI'[control];
    // Some org types require a numeric control, some a letter, some accept either.
    if ('ABEH'.includes(orgLetter)) return controlChar === String(control);
    if ('KPQS'.includes(orgLetter)) return controlChar === controlLetter;
    return controlChar === String(control) || controlChar === controlLetter;
  }

  return false;
};

/**
 * Per-country checksum validators, keyed by the VAT prefix used in the full
 * number (see `countryData` — e.g. 'PT', 'ES', 'EL'). Each receives the national
 * number WITHOUT the prefix and returns whether the control digit(s) are valid.
 *
 * PT and ES are implemented; add 'FR', … here to extend coverage.
 */
export const vatChecksumValidators: Record<string, (nationalNumber: string) => boolean> = {
  PT: isValidPtNif,
  ES: isValidEsVat,
};

export type VatValidationOptions = { required?: boolean };

/**
 * Validates a VAT/tax-id number. Returns an i18n KEY describing the error, or
 * null when the value is valid.
 *  - empty + required      → 'required_vat_number'
 *  - fails the format regex → 'invalid_vat_number'
 *  - fails the checksum      → 'invalid_vat_number'
 *
 * @param prefix country VAT prefix (e.g. 'PT'); typically `taxId.prefix`
 * @param number the national number the user typed (without prefix)
 */
export const getVatNumberError = (
  prefix: string | null | undefined,
  number: string | null | undefined,
  { required = true }: VatValidationOptions = {},
): string | null => {
  const value = (number || '').trim();
  if (!value) return required ? 'required_vat_number' : null;

  const safePrefix = prefix || '';
  const full = `${safePrefix}${value}`;
  if (!taxRegexp.test(full)) return 'invalid_vat_number';

  const checksum = vatChecksumValidators[safePrefix];
  if (checksum && !checksum(value)) return 'invalid_vat_number';

  return null;
};

/** Convenience boolean wrapper around {@link getVatNumberError}. */
export const isVatNumberValid = (
  prefix: string | null | undefined,
  number: string | null | undefined,
  options?: VatValidationOptions,
): boolean => getVatNumberError(prefix, number, options) === null;
